import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import CategoryCard from './components/CategoryCard';
import ProductCard from './components/ProductCard';
import FilterPanel from './components/FilterPanel';

import QuickViewModal from './components/QuickViewModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useCart } from '../../contexts/CartContext';
import notificationService from '../../utils/notificationService';

import { productService } from '../../services/productService';

const OfficialStore = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    priceRange: [0, 500000],
    brands: [],
    minRating: null
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);

  // ─── MASTER STATE: fetched once, NEVER mutated ───────────────────────────
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use CartContext instead of local state
  const {
    cartItems,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    closeCart,
    openCart
  } = useCart();

  // Fetch ALL active products once on mount — no filters applied here
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await productService?.getAll();
        setAllProducts(data || []);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err?.message || 'Ürünler yüklenirken bir hata oluştu');
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAllProducts();
  }, []);

  // Deep link: open modal automatically if ?product= param exists in URL
  useEffect(() => {
    if (loading || allProducts?.length === 0) return;
    const productId = searchParams?.get('product');
    if (productId) {
      const found = allProducts?.find(p => String(p?.id) === String(productId));
      if (found) {
        setQuickViewProduct(found);
      }
    }
  }, [loading, allProducts]);

  // Fetch available brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const brands = await productService?.getBrands();
        setAvailableBrands(brands || []);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  // ─── CATEGORY COUNTS: always from allProducts (immutable master list) ────
  const categories = useMemo(() => [
    { id: 'all', name: 'Tümü', icon: 'Grid3x3', count: allProducts?.length || 0 },
    { id: 'SUP ve Kano', name: 'SUP ve Kano', icon: 'Waves', count: allProducts?.filter(p => p?.category === 'SUP ve Kano')?.length || 0 },
    { id: 'Sup Motorları ve Kürek', name: 'Sup Motorları ve Kürek', icon: 'Anchor', count: allProducts?.filter(p => p?.category === 'Sup Motorları ve Kürek')?.length || 0 },
    { id: 'Giyim', name: 'Giyim', icon: 'Shirt', count: allProducts?.filter(p => p?.category === 'Giyim')?.length || 0 },
    { id: 'Aksesuar ve Yedek Parça', name: 'Aksesuar ve Yedek Parça', icon: 'Package', count: allProducts?.filter(p => p?.category === 'Aksesuar ve Yedek Parça')?.length || 0 },
  ], [allProducts]);

  // ─── DERIVED DISPLAY LIST: filtered + sorted from allProducts ────────────
  const displayProducts = useMemo(() => {
    let result = [...allProducts];

    // 1. Category filter
    if (activeCategory && activeCategory !== 'all') {
      result = result?.filter(p => p?.category === activeCategory);
    }

    // 2. Search filter
    if (searchQuery?.trim()) {
      const q = searchQuery?.trim()?.toLowerCase();
      result = result?.filter(p => p?.title?.toLowerCase()?.includes(q));
    }

    // 3. Price range filter
    if (filters?.priceRange) {
      result = result?.filter(p => p?.price >= filters?.priceRange?.[0] && p?.price <= filters?.priceRange?.[1]);
    }

    // 4. Brand filter
    if (filters?.brands?.length > 0) {
      result = result?.filter(p => filters?.brands?.includes(p?.brand));
    }

    // 5. Min rating filter
    if (filters?.minRating) {
      result = result?.filter(p => (p?.averageRating || 0) >= filters?.minRating);
    }

    // 6. Sorting
    if (sortBy === 'price-asc') {
      result?.sort((a, b) => (a?.price || 0) - (b?.price || 0));
    } else if (sortBy === 'price-desc') {
      result?.sort((a, b) => (b?.price || 0) - (a?.price || 0));
    } else if (sortBy === 'newest') {
      result?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
    } else {
      // 'relevance' — 3-phase smart sort
      result?.sort((a, b) => {
        // Rule 1: Stock status — out-of-stock products go to the very bottom
        const aHasStock = (a?.stockQuantity || 0) > 0;
        const bHasStock = (b?.stockQuantity || 0) > 0;
        if (aHasStock && !bHasStock) return -1;
        if (!aHasStock && bHasStock) return 1;

        // Rule 2: Featured — among in-stock products, featured ones come first
        if (a?.isFeatured && !b?.isFeatured) return -1;
        if (!a?.isFeatured && b?.isFeatured) return 1;

        // Rule 3: Date — remaining products sorted newest first
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      });
    }

    return result;
  }, [allProducts, activeCategory, searchQuery, filters, sortBy]);

  const sortOptions = [
    { value: 'relevance', label: 'Önerilen' },
    { value: 'price-asc', label: 'Fiyat: Düşükten Yükseğe' },
    { value: 'price-desc', label: 'Fiyat: Yüksekten Düşüğe' },
    { value: 'newest', label: 'En Yeni' }
  ];

  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem('filters', JSON.stringify(filters));
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      priceRange: [0, 500000],
      brands: [],
      minRating: null
    });
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    // Show toast notification
    const isBundle = !!(product?.is_bundle || product?.isBundle);
    const bundleCount = isBundle ? (product?.bundle_items || product?.bundleItems || [])?.length : 0;
    const toastMsg = isBundle && bundleCount > 0
      ? `📦 ${product?.title} paketi sepete eklendi! (${bundleCount} ürün)`
      : `${product?.title} sepete eklendi!`;
    setToastMessage(toastMsg);
    setTimeout(() => setToastMessage(null), 3000);
    notificationService?.storeNotification(
      'Ürün sepete eklendi',
      {
        body: `${product?.title} sepetinize eklendi`,
        tag: 'cart',
        priority: 'low'
      }
    );
  };

  const handleUpdateQuantity = (productId, amount) => {
    updateQuantity(productId, amount);
  };

  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
  };

  const handleToggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev?.includes(productId)
        ? prev?.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleOpenQuickView = (product) => {
    setQuickViewProduct(product);
    setSearchParams({ product: product?.id });
  };

  const handleCloseQuickView = () => {
    setQuickViewProduct(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next?.delete('product');
      return next;
    });
  };

  return (
    <>
      <Header />
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-xl shadow-2xl animate-fade-in-up">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
              Resmi Mağaza
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
              Profesyonel SUP ekipmanları, aksesuarlar ve giyim ürünlerinde güvenilir alışveriş deneyimi
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories?.map((category) => (
                <CategoryCard
                  key={category?.id}
                  category={category}
                  isActive={activeCategory === category?.id}
                  onClick={() => {
                    setActiveCategory(category?.id);
                    setFilters({ priceRange: [0, 500000], brands: [], minRating: null });
                  }}
                />
              ))}
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Icon
                  name="Search"
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Ürün ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 md:py-3.5 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-3 rounded-xl border border-border bg-card hover:bg-muted transition-colors"
                >
                  <Icon name="SlidersHorizontal" size={20} />
                  <span className="text-sm font-medium">Filtrele</span>
                </button>
                <div className="flex-1 md:w-64">
                  <Select
                    options={sortOptions}
                    value={sortBy}
                    onChange={setSortBy}
                    placeholder="Sırala"
                  />
                </div>
                <button
                  onClick={() => openCart()}
                  className="relative flex items-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <Icon name="ShoppingCart" size={20} />
                  {cartItems?.length > 0 && (
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-error text-error-foreground text-xs font-bold flex items-center justify-center">
                      {cartItems?.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/20">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-error mb-1">Hata</h3>
                  <p className="text-sm text-error/80">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm md:text-base text-muted-foreground">
              {loading ? (
                <span>Ürünler yükleniyor...</span>
              ) : (
                <>
                  <span className="font-semibold text-foreground">{displayProducts?.length || 0}</span> ürün bulundu
                </>
              )}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Shield" size={14} />
                <span>Güvenli Ödeme</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Truck" size={14} />
                <span>Hızlı Kargo</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                availableBrands={availableBrands}
              />
            </div>

            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex items-center justify-center py-16 md:py-24">
                  <LoadingSpinner size="large" />
                </div>
              ) : displayProducts?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 md:py-24">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-muted flex items-center justify-center mb-6">
                    <Icon name="Package" size={40} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                    Ürün bulunamadı
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6 text-center max-w-md">
                    Aradığınız kriterlere uygun ürün bulunamadı. Filtreleri değiştirmeyi deneyin.
                  </p>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Filtreleri Temizle
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {displayProducts?.map((product) => (
                    <ProductCard
                      key={product?.id}
                      product={product}
                      onAddToCart={handleAddToCart}
                      onQuickView={handleOpenQuickView}
                      onToggleWishlist={handleToggleWishlist}
                      isInWishlist={wishlist?.includes(product?.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 md:mt-16 p-6 md:p-8 lg:p-12 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Truck" size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  Ücretsiz Kargo
                </h3>
                <p className="text-sm text-muted-foreground">
                  500₺ ve üzeri alışverişlerde kargo bedava
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="RotateCcw" size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  Kolay İade
                </h3>
                <p className="text-sm text-muted-foreground">
                  14 gün içinde ücretsiz iade hakkı
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  Güvenli Ödeme
                </h3>
                <p className="text-sm text-muted-foreground">
                  SSL sertifikalı güvenli ödeme altyapısı
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuickViewModal
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={handleCloseQuickView}
        onAddToCart={handleAddToCart}
      />
    </>
  );
};

export default OfficialStore;